import {
    Avatar,
    Box,
    Divider,
    IconButton,
    Link,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material';
import { Cog, LayoutDashboard, LogOut } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { useAuthSession } from '~/contexts/auth/AuthSessionContext';
import { checkIsInSessionRouteWithoutRouter } from '~/hooks/session/useIsInSessionRoute';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import AppConfig from '~/utils/appconfig';

export default function ExternalLinksMenu() {
    const intl = useIntl();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { userName: accountName, userSignInName, logOut } = useAuthSession();
    const sessionData = useRecoilValue(sessionDataAtom);

    const handleLogout = () => {
        void logOut();
    };

    const handleClose = () => setAnchorEl(null);

    const isInConnectorRoute = checkIsInSessionRouteWithoutRouter();
    const userName = isInConnectorRoute ? sessionData?.username : accountName;
    const userInitial = userName ? userName.charAt(0).toUpperCase() : '';

    return (
        <>
            <Tooltip title={<FormattedMessage id="accountMenu" />}>
                <IconButton
                    aria-label={intl.formatMessage({ id: 'accountMenu' })}
                    aria-haspopup="true"
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    data-testid="external-links-button"
                    size="small"
                >
                    {userInitial ? (
                        <Avatar
                            sx={(theme) => ({
                                bgcolor: theme.palette.primary.main,
                                width: 24,
                                height: 24,
                                fontSize: 14,
                                color: 'white',
                            })}
                        >
                            {userInitial}
                        </Avatar>
                    ) : (
                        <Avatar sx={{ width: 24, height: 24 }} />
                    )}
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            marginLeft: '6.5px',
                        },
                    },
                }}
            >
                <Link
                    href={AppConfig.dashboardUrl}
                    sx={{ textDecoration: 'none', color: 'inherit' }}
                    target="_blank"
                    rel="noreferrer"
                >
                    <MenuItem onClick={handleClose}>
                        <LayoutDashboard size={12} />
                        Dashboard
                    </MenuItem>
                </Link>
                <Link
                    href={AppConfig.configUrl}
                    sx={{ textDecoration: 'none', color: 'inherit' }}
                    target="_blank"
                    rel="noreferrer"
                >
                    <MenuItem onClick={handleClose}>
                        <Cog size={12} />
                        Configuration
                    </MenuItem>
                </Link>

                <MenuItem onClick={handleLogout}>
                    <LogOut size={12} />
                    Log out
                </MenuItem>
                <Divider sx={{ my: 0, py: 0 }} />
                <Box display="flex" alignItems="center" gap={1} px={0.5}>
                    <Avatar
                        sx={(theme) => ({
                            bgcolor: theme.palette.primary.main,
                            width: 24,
                            height: 24,
                            fontSize: 14,
                            color: 'white',
                        })}
                    >
                        {userInitial}
                    </Avatar>
                    <Box>
                        <Typography
                            fontSize={12}
                            variant="body2"
                            lineHeight={1}
                        >
                            {userName || 'User'}
                        </Typography>
                        {isInConnectorRoute ? (
                            <Typography fontSize={10} variant="body2">
                                Workstation session
                            </Typography>
                        ) : (
                            <Typography fontSize={10} variant="body2">
                                {userSignInName}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Menu>
        </>
    );
}
